/**
 * @fileoverview Displays a conversation thread with a specific phone number.
 * Shows message history, opt-out status, and provides a composer for sending messages.
 */

import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { Phone, ExternalLink, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';
import { formatPhoneNumber, useConversationThread, useOptOutStatus, useSendSMS } from '@/hooks/useMessages';
import type { ConversationThread as ConversationThreadType } from '@/types/message';

interface ConversationThreadProps {
  /** The conversation thread data (may be null if not yet loaded) */
  thread: ConversationThreadType | null;
  /** The phone number for this conversation */
  phoneNumber: string | null;
}

/**
 * Renders a complete conversation thread with header, messages, and composer.
 */
export function ConversationThread({ thread, phoneNumber }: ConversationThreadProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { data: messages, isLoading } = useConversationThread(phoneNumber);
  const { data: optOutStatus } = useOptOutStatus(phoneNumber);
  const sendSMS = useSendSMS();

  const isOptedOut = !!optOutStatus;

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages]);

  if (!phoneNumber) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Select a conversation to view messages</p>
        </div>
      </div>
    );
  }

  const handleSend = (messageBody: string) => {
    sendSMS.mutate({
      toNumber: phoneNumber,
      messageBody,
      callId: thread?.linked_call_id || undefined,
    });
  };

  // Group messages by date
  const messagesByDate = (messages || []).reduce(
    (acc, msg) => {
      const date = format(new Date(msg.sent_at), 'MMMM d, yyyy');
      if (!acc[date]) acc[date] = [];
      acc[date].push(msg);
      return acc;
    },
    {} as Record<string, typeof messages>
  );

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Thread Header */}
      <div className="flex-shrink-0 bg-background border-b border-border p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Phone className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">
              {formatPhoneNumber(phoneNumber)}
            </h2>
            <div className="flex items-center gap-2">
              {isOptedOut && (
                <Badge variant="destructive" className="text-xs">
                  <Ban className="w-3 h-3 mr-1" />
                  Opted Out
                </Badge>
              )}
              {thread?.linked_call_id && (
                <Link
                  to={`/calls/${thread.linked_call_id}`}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  View linked call
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages - using native scrollable div for reliable scroll-to-bottom */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto p-4"
      >
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <Skeleton className="h-16 w-48 rounded-2xl" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(messagesByDate).map(([date, msgs]) => (
              <div key={date}>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground font-medium">{date}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-4">
                  {msgs?.map((msg) => <MessageBubble key={msg.id} message={msg} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <MessageComposer
        onSend={handleSend}
        isOptedOut={isOptedOut}
        isSending={sendSMS.isPending}
        disabled={!phoneNumber}
      />
    </div>
  );
}
